import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import StepShell from '@/components/StepShell';
import OptionCard from '@/components/OptionCard';
import AmbientCard, { Glyphs } from '@/components/AmbientCard';
import Ornament from '@/components/Ornament';
import Scale from '@/components/Scale';
import { useSession } from '@/store/session';
import type { NotaFamiliar, Preferences as PrefsT } from '@/types';

const AMBIENTS: Array<{
  id: PrefsT['q1_ambiente'];
  label: string;
  desc: string;
  gradient: string;
  glyph: React.ReactNode;
}> = [
  {
    id: 'desierto_atardecer',
    label: 'Desierto al atardecer',
    desc: 'Cálido · misterioso',
    gradient: 'linear-gradient(160deg, #D87C3A 0%, #B8552B 40%, #4A1F08 100%)',
    glyph: Glyphs.Sun,
  },
  {
    id: 'jardin_andalusi',
    label: 'Jardín andalusí',
    desc: 'Floral · fresco',
    gradient: 'linear-gradient(160deg, #1B6B53 0%, #0F4D3A 60%, #082B22 100%)',
    glyph: Glyphs.Flower,
  },
  {
    id: 'mercado_especias',
    label: 'Mercado de especias',
    desc: 'Especiado · envolvente',
    gradient: 'linear-gradient(160deg, #A82D2D 0%, #6B1212 50%, #2B0808 100%)',
    glyph: Glyphs.Star,
  },
  {
    id: 'bosque_lluvia',
    label: 'Bosque tras la lluvia',
    desc: 'Amaderado · limpio',
    gradient: 'linear-gradient(160deg, #2D5A3F 0%, #1A3A2A 50%, #0B1F15 100%)',
    glyph: Glyphs.Leaf,
  },
];

const PALABRAS: Array<{ id: PrefsT['q2_palabra']; label: string }> = [
  { id: 'elegante',   label: 'Elegante' },
  { id: 'sensual',    label: 'Sensual' },
  { id: 'fresco',     label: 'Fresco' },
  { id: 'misterioso', label: 'Misterioso' },
];

const DULCE_SECO: Array<{ id: PrefsT['q4_dulce_seco']; label: string; desc: string }> = [
  { id: 'dulce', label: 'Dulce',    desc: 'Vainilla, gourmand, cremoso' },
  { id: 'medio', label: 'Punto medio', desc: 'Equilibrio' },
  { id: 'seco',  label: 'Seco',     desc: 'Maderas, ámbar, especiados' },
];

const NOTAS: Array<{ id: NotaFamiliar; label: string; icon: string }> = [
  { id: 'vainilla', label: 'Vainilla', icon: '◔' },
  { id: 'citrico',  label: 'Cítrico',  icon: '◐' },
  { id: 'madera',   label: 'Madera',   icon: '⊿' },
  { id: 'floral',   label: 'Floral',   icon: '✿' },
  { id: 'oud',      label: 'Oud',      icon: '◆' },
];

const STELA_LABELS = ['Mínima', 'Discreta', 'Equilibrada', 'Marcada', 'Poderosa'];
const INTENSITY_LABELS = ['Ligero', 'Suave', 'Marcado', 'Muy intenso'];

const ORIENTAL_FRESCO: Array<{ id: PrefsT['q7_oriental_fresco']; label: string; desc: string }> = [
  { id: 'oriental', label: 'Oriental', desc: 'Especias, ámbar, oud' },
  { id: 'medio',    label: 'Punto medio', desc: 'Sin preferencia clara' },
  { id: 'fresco',   label: 'Fresco occidental', desc: 'Cítricos, aromáticos' },
];

const ESTILO: Array<{ id: PrefsT['q8_estilo']; label: string; desc: string }> = [
  { id: 'al_pasar', label: 'Que se note al pasar', desc: 'Estela poderosa' },
  { id: 'medio',    label: 'Punto medio',           desc: 'Presencia equilibrada' },
  { id: 'intimo',   label: 'Íntimo',                desc: 'Solo se huele de cerca' },
];

export default function Preferences() {
  const navigate = useNavigate();
  const { preferences: p, setPreferences } = useSession();

  const notas = p.q5_notas_familiares ?? [];
  const toggleNota = useCallback(
    (id: NotaFamiliar) => {
      const next = notas.includes(id) ? notas.filter((n) => n !== id) : [...notas, id];
      setPreferences({ q5_notas_familiares: next });
    },
    [notas, setPreferences]
  );

  const isValid = useMemo(
    () => Boolean(
      p.q1_ambiente && p.q2_palabra && p.q3_estela &&
      p.q4_dulce_seco && notas.length > 0 && p.q6_intensidad_habitual &&
      p.q7_oriental_fresco && p.q8_estilo
    ),
    [p, notas]
  );

  return (
    <StepShell
      step={4}
      title="Tus preferencias"
      subtitle="Ocho preguntas. Lo importante es lo que sientes, no lo que crees que deberías decir."
      onBack={() => navigate('/piel')}
      onNext={() => navigate('/mi-perfume')}
      nextDisabled={!isValid}
    >
      {/* Q1 — Ambiente */}
      <Question num={1} title="¿Qué ambiente te atrae más?">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {AMBIENTS.map((a) => (
            <AmbientCard
              key={a.id}
              label={a.label}
              description={a.desc}
              gradient={a.gradient}
              glyph={a.glyph}
              active={p.q1_ambiente === a.id}
              onClick={() => setPreferences({ q1_ambiente: a.id })}
            />
          ))}
        </div>
      </Question>

      {/* Q2 — Palabra clave */}
      <Question num={2} title="Una palabra para cómo quieres que te recuerden">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {PALABRAS.map((w) => (
            <OptionCard
              key={w.id}
              size="lg"
              label={w.label}
              active={p.q2_palabra === w.id}
              onClick={() => setPreferences({ q2_palabra: w.id })}
            />
          ))}
        </div>
      </Question>

      {/* Q3 — Estela */}
      <Question num={3} title="¿Cuánta presencia quieres que tenga?">
        <Scale
          steps={5}
          stepLabels={STELA_LABELS}
          value={p.q3_estela}
          onChange={(v) => setPreferences({ q3_estela: v as PrefsT['q3_estela'] })}
        />
      </Question>

      {/* Q4 — Dulce/seco */}
      <Question num={4} title="¿Prefieres fragancias dulces o secas?">
        <div className="grid grid-cols-3 gap-3">
          {DULCE_SECO.map((o) => (
            <OptionCard
              key={o.id}
              size="lg"
              label={o.label}
              description={o.desc}
              active={p.q4_dulce_seco === o.id}
              onClick={() => setPreferences({ q4_dulce_seco: o.id })}
            />
          ))}
        </div>
      </Question>

      {/* Q5 — Notas familiares (multi-select) */}
      <Question num={5} title="¿Qué notas te resultan más familiares?" hint="Puedes elegir varias">
        <div className="grid grid-cols-5 gap-3">
          {NOTAS.map((n) => (
            <OptionCard
              key={n.id}
              size="lg"
              label={n.label}
              icon={<span className="text-3xl">{n.icon}</span>}
              active={notas.includes(n.id)}
              onClick={() => toggleNota(n.id)}
            />
          ))}
        </div>
      </Question>

      {/* Q6 — Intensidad habitual */}
      <Question num={6} title="¿Cómo es tu perfume habitual?">
        <Scale
          steps={4}
          stepLabels={INTENSITY_LABELS}
          value={p.q6_intensidad_habitual}
          onChange={(v) => setPreferences({ q6_intensidad_habitual: v as PrefsT['q6_intensidad_habitual'] })}
        />
      </Question>

      {/* Q7 — Oriental vs fresco */}
      <Question num={7} title="¿Te llaman más los perfumes orientales o los frescos occidentales?">
        <div className="grid grid-cols-3 gap-3">
          {ORIENTAL_FRESCO.map((o) => (
            <OptionCard
              key={o.id}
              size="lg"
              label={o.label}
              description={o.desc}
              active={p.q7_oriental_fresco === o.id}
              onClick={() => setPreferences({ q7_oriental_fresco: o.id })}
            />
          ))}
        </div>
      </Question>

      {/* Q8 — Estilo */}
      <Question num={8} title="¿Qué prefieres en una fragancia?" last>
        <div className="grid grid-cols-3 gap-3">
          {ESTILO.map((o) => (
            <OptionCard
              key={o.id}
              size="lg"
              label={o.label}
              description={o.desc}
              active={p.q8_estilo === o.id}
              onClick={() => setPreferences({ q8_estilo: o.id })}
            />
          ))}
        </div>
      </Question>

      {!isValid && (
        <p className="mt-6 text-xs text-elixir-cream/45 text-center">
          Responde las 8 preguntas para continuar.
        </p>
      )}
    </StepShell>
  );
}

function Question({
  num,
  title,
  hint,
  children,
  last,
}: {
  num: number;
  title: string;
  hint?: string;
  children: React.ReactNode;
  last?: boolean;
}) {
  return (
    <section className={last ? 'mb-2' : ''}>
      <header className="text-center mb-5">
        <p className="label-section mb-2">Pregunta {num} / 8</p>
        <h3 className="serif italic text-elixir-cream text-2xl leading-tight max-w-2xl mx-auto">
          {title}
        </h3>
        {hint && (
          <p className="text-xs text-elixir-cream/55 mt-1.5 italic serif">{hint}</p>
        )}
      </header>
      {children}
      {!last && <Ornament />}
    </section>
  );
}
